import React, { useEffect, useRef, useState } from 'react'
import { Table, Button, ButtonGroup } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'

import { useLanguage } from '../../contexts/LanguageContext'
import { POI_PATH } from '../../config/routes'
import {
  slugify,
  getPoiLabel,
  getPoiColor,
  getPoiMarkerPath,
  getGpsxMax,
  getGpsxMin,
  getGpsyMax,
  getGpsyMin,
  calculateZoom,
  getLevel,
} from '../../utils/utils'
import config from '../../config/config'
import DisplayPois from '../PointsOfInterest/DisplayPois'

const RidingList = ({ pois }) => {
  const { t } = useTranslation()
  const { activeLanguage } = useLanguage()

  const mapRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const infoWindowRef = useRef(null)
  const markersRef = useRef([]) // contiendra { poiId, marker, polyline }

  const getStoredHiddenPois = () => {
    const stored = localStorage.getItem('hiddenPois')
    return stored ? JSON.parse(stored) : []
  }

  const [displayedPois, setDisplayedPois] = useState(() => {
    const hidden = getStoredHiddenPois()
    return pois
      .filter((p) => p.status !== 'DRAFT')
      .map((p) => ({ ...p, isDisplayed: !hidden.includes(p.id) }))
  })

  // Initialise la map + markers + polylines
  useEffect(() => {
    if (!pois || pois.length === 0) return

    const loadGoogleMapsScript = () => {
      if (
        !document.querySelector(
          'script[src*="maps.googleapis.com/maps/api/js"]',
        )
      ) {
        const script = document.createElement('script')
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
        script.async = true
        script.defer = true
        script.onload = initMap
        document.head.appendChild(script)
      } else {
        initMap()
      }
    }

    const initMap = () => {
      if (!window.google || !window.google.maps) return

      const gpsxMax = getGpsxMax(pois) + 0.01
      const gpsxMin = getGpsxMin(pois)
      const gpsyMax = getGpsyMax(pois)
      const gpsyMin = getGpsyMin(pois)

      const mapOptions = {
        zoom:
          gpsxMax && gpsxMin && gpsyMax && gpsyMin
            ? calculateZoom(gpsxMax, gpsxMin, gpsyMax, gpsyMin)
            : config.mapPoiZoom,
        center: {
          lat: (gpsxMax + gpsxMin) / 2,
          lng: (gpsyMax + gpsyMin) / 2,
        },
        mapTypeId: 'terrain',
        styles: [
          {
            featureType: 'all',
            elementType: 'labels',
            stylers: [{ visibility: 'on' }],
          },
          {
            featureType: 'landscape.natural',
            elementType: 'geometry',
            stylers: [{ visibility: 'simplified' }, { color: '#7FBF7F' }],
          },
        ],
      }

      // crée la map
      const map = new window.google.maps.Map(mapRef.current, mapOptions)
      mapInstanceRef.current = map

      // infowindow pour les popups
      infoWindowRef.current = new window.google.maps.InfoWindow({
        disableAutoPan: false,
        boxClass: 'infoWindowBox',
      })
      infoWindowRef.current.addListener('closeclick', resetPolylineOpacity)

      // ajoute marqueurs + polylines
      drawPoisOnMap()
    }

    loadGoogleMapsScript()
  }, [pois])

  // redessine markers & polylines quand displayedPois change
  useEffect(() => {
    if (mapInstanceRef.current) {
      drawPoisOnMap()
    }
  }, [displayedPois])

  // met à jour écrit au localStorage et redraw
  const handleToggleDisplay = (poiId, shouldDisplay) => {
    const updated = displayedPois.map((p) =>
      p.id === poiId ? { ...p, isDisplayed: shouldDisplay } : p,
    )
    setDisplayedPois(updated)
    localStorage.setItem(
      'hiddenPois',
      JSON.stringify(updated.filter((p) => !p.isDisplayed).map((p) => p.id)),
    )
  }

  // dessine tous les POIs
  const drawPoisOnMap = () => {
    const map = mapInstanceRef.current
    const bounds = new window.google.maps.LatLngBounds()

    // libère anciens markers/polylines
    markersRef.current.forEach(({ marker, polyline }) => {
      marker.setMap(null)
      if (polyline) polyline.setMap(null)
    })
    markersRef.current = []

    displayedPois.forEach((poi) => {
      addMarkerAndPolyline(poi, map, bounds)
    })

    map.fitBounds(bounds)
  }

  const addMarkerAndPolyline = (poi, map, bounds) => {
    const position = { lat: poi.gpsx, lng: poi.gpsy }
    const marker = new window.google.maps.Marker({
      position,
      map: poi.isDisplayed ? map : null,
      title: poi.name,
      icon: getPoiMarkerPath(poi),
    })
    bounds.extend(position)

    // filtre des points valides
    const valid = Array.isArray(poi.gpxDatas)
      ? poi.gpxDatas.filter(
          (point) =>
            Array.isArray(point) &&
            point.length >= 2 &&
            typeof point[0] === 'number' &&
            typeof point[1] === 'number',
        )
      : []

    let polyline = null
    if (valid.length > 1) {
      const path = valid.map((pt) => ({ lat: pt[0], lng: pt[1] }))
      const color = (() => {
        switch (getLevel(poi)) {
          case 'BLUE':
            return 'blue'
          case 'GREEN':
            return 'yellow'
          case 'BLACK':
            return 'black'
          case 'RED':
            return 'red'
          default:
            return 'grey'
        }
      })()

      polyline = new window.google.maps.Polyline({
        path,
        geodesic: true,
        strokeColor: color,
        strokeOpacity: poi.isDisplayed ? 1 : 0,
        strokeWeight: 2,
      })
      polyline.setMap(map)
    }

    markersRef.current.push({ poiId: poi.id, marker, polyline })

    marker.addListener('click', () => {
      // ouverture infowindow
      const url = POI_PATH.replace(':slug', slugify(poi.labelFr)).replace(
        ':id',
        poi.id,
      )
      infoWindowRef.current.setContent(`
        <div>
          <h4>${getPoiLabel(poi, activeLanguage)}</h4>
          <div style="text-align:center;">
            <button class="btn btn-primary" onclick="window.location.href='${url}'">
              ${t('SHOW')}
            </button>
            <button class="btn btn-secondary" onclick="document.getElementById('hide-poi-${
              poi.id
            }').click()">
              ${t('HIDE')}
            </button>
          </div>
        </div>
      `)
      infoWindowRef.current.open(map, marker)
      highlightPolyline(poi.id)
    })
  }

  // met à jour l'opacité des polylines selon la sélection
  const highlightPolyline = (selectedId) => {
    markersRef.current.forEach(({ poiId, polyline }) => {
      if (polyline) {
        polyline.setOptions({ strokeOpacity: poiId === selectedId ? 1 : 0.5 })
      }
    })
  }

  const resetPolylineOpacity = () => {
    markersRef.current.forEach(({ polyline }) => {
      if (polyline) polyline.setOptions({ strokeOpacity: 1 })
    })
  }

  return (
    <div className="poi-list-wrapper mt-3 mb-3">
      <Table bordered className="rdb-table">
        <thead>
          <tr>
            <th>{t('N°')}</th>
            <th className="poi-list-table-header-left">{t('LABEL')}</th>
            <th>{t('ALTITUDE_MIN_LABEL')}</th>
            <th>{t('ALTITUDE_MAX_LABEL')}</th>
            <th>{t('DISPLAYED')} ?</th>
          </tr>
        </thead>
        <tbody>
          {displayedPois.map((poi) => (
            <tr key={poi.id}>
              <td className="text-center">
                <Link
                  to={POI_PATH.replace(':slug', slugify(poi.labelFr)).replace(
                    ':id',
                    poi.id,
                  )}
                  className="poi-list-numero-link btn"
                  style={{
                    '--background-color': getPoiColor(poi),
                    '--background-color-rgb': getPoiColor(poi),
                  }}
                >
                  {poi.numero}
                </Link>
              </td>
              <td>{getPoiLabel(poi, activeLanguage)}</td>
              <td className="text-center">{poi.altiMin}m</td>
              <td className="text-center">{poi.altiMax}m</td>
              <td className="text-center">
                <ButtonGroup className="mb-2" style={{ height: '32px' }}>
                  <Button
                    variant={poi.isDisplayed ? 'primary' : 'secondary'}
                    onClick={() => handleToggleDisplay(poi.id, true)}
                    className={`btn-sm poi-list-btn-sm-custom ${
                      poi.isDisplayed ? 'activated-noclic' : ''
                    }`}
                  >
                    <FontAwesomeIcon icon={faEye} />
                  </Button>
                  <Button
                    id={`hide-poi-${poi.id}`}
                    variant={!poi.isDisplayed ? 'primary' : 'secondary'}
                    onClick={() => handleToggleDisplay(poi.id, false)}
                    className={`btn-sm poi-list-btn-sm-custom ${
                      !poi.isDisplayed ? 'activated-noclic' : ''
                    }`}
                  >
                    <FontAwesomeIcon icon={faEyeSlash} />
                  </Button>
                </ButtonGroup>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div id="map" ref={mapRef} style={{ height: '500px', width: '100%' }} />

      <hr />
      <DisplayPois pois={pois} isCity={true} isCategory={false} />
    </div>
  )
}

export default RidingList
